
import { computed, defineComponent, onMounted, PropType, reactive, watch } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'

// validations
import {
    AdditionalServicesFTLI,
    AdditionalServiceShortModalFTLI,
} from '@/core/types/additional_services.types'

import { DowntimeFullT, FullFTLOrderT } from '@/core/types/orders.types'

import useVuelidate from '@vuelidate/core'
import { requiredIf, required } from '@vuelidate/validators'
import { billing } from '@/core/realisations/billing.realisation'
import {
    FtlCalculatorsAdditionalService,
    FtlCalculatorsAdditionalServiceResponse,
    GetFtlCalculatorsResponse,
    GetFtlTariffsData,
} from '@/core/types/billing.types'
import useNotifications from '@/common/composable/useNotifications'

const ADDITIONAL_SERVICES: { [key: string]: keyof FtlCalculatorsAdditionalServiceResponse } = {
    soft_package: 'softPackageCost',
    grid: 'gridCost',
    loader: 'loaderCost',
    manipulator: 'manipulatorCost',
    crane: 'craneCost',
    hydraulic_trolley: 'hydraulicTrolleyCost',
    pallet: 'palletCost',
}

export default defineComponent({
    components: {
        Modal,
        TheInput,
        Loader,
    },
    emits: ['cancel', 'submitted'],
    props: {
        isLoading: {
            type: Boolean,
            default: false,
        },
        additionalServices: {
            type: Array as PropType<AdditionalServicesFTLI[]>,
            default: () => [],
        },
        stocks: {
            type: Array as PropType<{ additional_service: AdditionalServicesFTLI[] }[]>,
            default: () => [],
        },
        paymentRateTypeId: {
            type: Number,
            default: null,
        },
        downtime: {
            type: Object as PropType<DowntimeFullT>,
            default: null,
        },
        statusCode: {
            type: String,
            default: null,
        },
        senderAddress: {
            type: String,
            default: null,
        },
        ftlOrder: {
            type: Object as PropType<FullFTLOrderT>,
            default: null,
        },
    },
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            //
            finalCost: null as number | null,
            additionalServiceCost: null as number | null,
            //
            price: null as number | null,
            cost: null as number | null,
            minimumHour: null as number | null,
        })

        const delivery_state = reactive({
            additional_services: [] as AdditionalServiceShortModalFTLI[],
            stock_services: [] as AdditionalServiceShortModalFTLI[],
            has_downtime: false,
            paid_price_total: null as number | null,
            downtime: {
                paid_price: null as number | null,
            },
        })

        const isDelivered = computed(() => props.statusCode === 'delivered')

        const state_form$ = useVuelidate(
            {
                paid_price_total: {
                    required: requiredIf(() =>
                        Boolean(props.ftlOrder && props.ftlOrder.is_need_car)
                    ),
                },
                downtime: {
                    paid_price: {
                        required: requiredIf(
                            () => delivery_state.has_downtime && isDelivered.value
                        ),
                    },
                },
            },
            delivery_state
        )

        const emitCancel = () => {
            emit('cancel')
        }

        const getAdditionalServices = () => {
            const additional_services = delivery_state.additional_services.map((service) => {
                return {
                    id: service.id,
                    paid_price_per_hour: Number(service.paid_price_per_hour),
                    paid_price_total: Number(service.paid_price_total),
                    duration_client: Number(service.duration_client),
                }
            })

            delivery_state.stock_services.forEach((service) => {
                additional_services.push({
                    id: service.id,
                    paid_price_per_hour: Number(service.paid_price_per_hour),
                    paid_price_total: Number(service.paid_price_total),
                    duration_client: Number(service.duration_client),
                })
            })

            return additional_services
        }

        const emitSubmitted = () => {
            state_form$.value.$touch()
            if (
                !delivery_state.additional_services.every(
                    (s) => s.paid_price_total && s.duration_client && s.paid_price_per_hour
                )
            )
                return
            if (
                !delivery_state.stock_services.every(
                    (s) => s.paid_price_total && s.duration_client && s.paid_price_per_hour
                )
            )
                return
            if (state_form$.value.$invalid) return

            if (delivery_state.has_downtime) {
                emit('submitted', {
                    paid_price_total: delivery_state.paid_price_total,
                    additional_services: getAdditionalServices(),
                    has_downtime: delivery_state.has_downtime,
                    downtime: {
                        paid_price: Number(delivery_state.downtime.paid_price),
                    },
                })
            } else {
                emit('submitted', {
                    paid_price_total: delivery_state.paid_price_total,
                    additional_services: getAdditionalServices(),
                })
            }
        }

        const fillAdditionalServices = (
            response: GetFtlCalculatorsResponse,
            responseTariff: GetFtlTariffsData[]
        ) => {
            delivery_state.additional_services = []
            delivery_state.stock_services = []

            let servicesTake: FtlCalculatorsAdditionalServiceResponse | null = null
            if (response.result.additionalServiceTake) {
                servicesTake = response.result.additionalServiceTake
            }
            let servicesDelivery: FtlCalculatorsAdditionalServiceResponse | null = null
            if (response.result.additionalServiceDelivery) {
                servicesDelivery = response.result.additionalServiceDelivery
            }

            props.additionalServices?.forEach((service) => {
                let new_service: AdditionalServiceShortModalFTLI = {
                    id: service.id,
                    duration_client: service.duration_client,
                    type_name: service.type_name,
                    type_code: service.type_code,
                    value: service.value,
                    paid_price_per_hour: null,
                    paid_price_total: null,
                }
                const serviceFound = responseTariff.find((s) => s.name === service.type_code)

                if (serviceFound) {
                    new_service.paid_price_per_hour = serviceFound.price
                }
                if (servicesTake) {
                    new_service.paid_price_total =
                        servicesTake[ADDITIONAL_SERVICES[service.type_code as string]]
                }

                delivery_state.additional_services.push(new_service)
            })

            props.stocks?.forEach((stock) => {
                if (stock.additional_service && stock.additional_service.length) {
                    stock.additional_service.forEach((service) => {
                        let new_service: AdditionalServiceShortModalFTLI = {
                            id: service.id,
                            duration_client: service.duration_client,
                            type_name: service.type_name,
                            type_code: service.type_code,
                            value: service.value,
                            paid_price_per_hour: null,
                            paid_price_total: null,
                        }
                        const serviceFound = responseTariff.find(
                            (s) => s.name === service.type_code
                        )

                        if (serviceFound) {
                            new_service.paid_price_per_hour = serviceFound.price
                        }
                        if (servicesDelivery) {
                            new_service.paid_price_total =
                                servicesDelivery[ADDITIONAL_SERVICES[service.type_code as string]]
                        }

                        delivery_state.stock_services.push(new_service)
                    })
                }
            })
        }

        const getBillingPayload = () => {
            const additionalServicesTake = props.ftlOrder.additional_service.map((service) => {
                return {
                    code: service.type_code,
                    value: Number(service.value),
                    duration: Number(service.duration_client),
                    costPerHour: service.cost_per_hour,
                    costTotal: service.cost_total,
                    paidPricePerHour: service.paid_price_per_hour,
                    paidPriceTotal: service.paid_price_total,
                }
            })
            const endpoint = props.ftlOrder.stock_delivery_notes.find(
                (stock) => stock.receiver.is_endpoint
            )
            let additionalServicesDelivery: FtlCalculatorsAdditionalService[] = []
            if (endpoint) {
                additionalServicesDelivery = endpoint.additional_service.map((service) => {
                    return {
                        code: service.type_code,
                        value: Number(service.value),
                        duration: Number(service.duration_client),
                        costPerHour: service.cost_per_hour,
                        costTotal: service.cost_total,
                        paidPricePerHour: service.paid_price_per_hour,
                        paidPriceTotal: service.paid_price_total,
                    }
                })
            }

            return {
                zoneId: props.ftlOrder.zone_id,
                companyId: props.ftlOrder.company_id,
                volume: props.ftlOrder.car_info ? props.ftlOrder.car_info.load_capacity_value : 0,
                duration: props.ftlOrder.auto_duration,
                additionalServicesTake,
                additionalServicesDelivery,
            }
        }

        const fillBilling = (
            response: GetFtlCalculatorsResponse,
            responseTariff: GetFtlTariffsData[]
        ) => {
            if (response.result) {
                delivery_state.paid_price_total = response.result.deliveryCost || null
                delivery_state.downtime.paid_price = response.result.downtimeCost || null
                fillAdditionalServices(response, responseTariff)

                if (response.result.finalCost && response.result.finalCost !== 0) {
                    state.finalCost = response.result.finalCost
                }

                const result =
                    Number(state.finalCost) -
                    (Number(delivery_state.paid_price_total) +
                        Number(delivery_state.downtime.paid_price))
                state.additionalServiceCost = parseFloat(result.toFixed(2))
            }
        }

        const loadBilling = async () => {
            try {
                state.is_loading = true

                const paramsTariff = {
                    companyId: props.ftlOrder.company_id,
                    shipmentTariffId: 2,
                }

                const response = await billing.getFtlCalculators(getBillingPayload())
                const responseTariff = await billing.getFtlTariffs(paramsTariff)

                fillBilling(response, responseTariff.data)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении информации произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const loadShipmentCostsFtl = async () => {
            try {
                state.is_loading = true

                const params = {
                    companyId: props.ftlOrder.company_id,
                    volume: props.ftlOrder.car_info.load_capacity_value,
                    zoneId: props.ftlOrder.zone_id,
                }

                const response = await billing.getShipmentCostsFtl(params)

                if (response.data && response.data.length) {
                    state.price = response.data[0].price
                    state.cost = response.data[0].cost
                    state.minimumHour = response.data[0].minimumHour
                }
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении информации произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        watch(
            () => [delivery_state.paid_price_total, delivery_state.downtime.paid_price],
            () => {
                state.finalCost =
                    Number(delivery_state.paid_price_total) +
                    Number(delivery_state.downtime.paid_price) +
                    Number(state.additionalServiceCost)
            }
        )

        watch(
            () => delivery_state.additional_services.map((service) => service.paid_price_total),
            () => {
                const sumOne = delivery_state.additional_services.reduce(
                    (sum, service) => sum + Number(service.paid_price_total),
                    0
                )
                const sumTwo = delivery_state.stock_services.reduce(
                    (sum, service) => sum + Number(service.paid_price_total),
                    0
                )

                state.additionalServiceCost = sumOne + sumTwo
                state.finalCost =
                    Number(delivery_state.paid_price_total) +
                    Number(delivery_state.downtime.paid_price) +
                    Number(state.additionalServiceCost)
            }
        )

        watch(
            () => delivery_state.stock_services.map((service) => service.paid_price_total),
            () => {
                const sumOne = delivery_state.additional_services.reduce(
                    (sum, service) => sum + Number(service.paid_price_total),
                    0
                )
                const sumTwo = delivery_state.stock_services.reduce(
                    (sum, service) => sum + Number(service.paid_price_total),
                    0
                )

                state.additionalServiceCost = sumOne + sumTwo
                state.finalCost =
                    Number(delivery_state.paid_price_total) +
                    Number(delivery_state.downtime.paid_price) +
                    Number(state.additionalServiceCost)
            }
        )

        const downtimePlaceholder = computed(() => {
            return `Выставление за простой ((${Number(props.ftlOrder.auto_duration)} - ${Number(
                state.minimumHour
            )}) x ${Number(state.cost)} x 1.16)`
        })
        const paidPricePlaceholder = computed(() => {
            return `Выставление за перевозку (${Number(state.price)} x 1.16)`
        })
        const stocksFiltered = computed(() => {
            return props.stocks.filter(
                (stock) => stock.additional_service && stock.additional_service.length
            )
        })

        onMounted(() => {
            loadBilling()
            loadShipmentCostsFtl()
            if (isDelivered.value && props.downtime) {
                delivery_state.has_downtime = true
            }
        })

        return {
            state,
            state_form$,
            delivery_state,
            emitCancel,
            emitSubmitted,
            isDelivered,
            downtimePlaceholder,
            paidPricePlaceholder,
            stocksFiltered,
        }
    },
})
